import { useState, useEffect, useRef, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f3f9ec", icon:"🏪" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e8", icon:"📦" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#ebf2ff", icon:"🛒" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f4", icon:"👗" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089", bg:"#fdf0fb", icon:"🛍️" },
];

const DEFAULT_PACKER  = "Vasudeva Enterprises";
const DEFAULT_ADDR    = "Tower 4C, Landcraft Golflinks, Pandav Nagar, Ghaziabad, Uttar Pradesh 201001";
const DEFAULT_ORIGIN  = "India";
const DEFAULT_BRAND   = "Leather Patta";
const DEFAULT_TAGLINE = "Genuine Leather · Crafted in India";
const DEFAULT_CONTACT = "info@3june.in · +91 82872 54548";
const DEFAULT_WEBSITE = "leatherpatta.com";
const DEFAULT_MFG    = () => { const d=new Date(); return String(d.getMonth()+1).padStart(2,"0")+"/"+d.getFullYear(); };

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:12, verticalAlign:"middle" },
};

// ── Normalize orders from each platform into common format ────
function normalizeOrder(order, platform) {
  if (platform === "shopify") {
    const addr = order.shipping_address || {};
    return {
      id:          String(order.id),
      name:        order.name || "#"+order.id,
      platform:    "shopify",
      date:        order.created_at,
      customer:    order.customer ? order.customer.first_name+" "+order.customer.last_name : addr.name || "Guest",
      phone:       addr.phone || order.customer?.phone || "",
      shipTo:      [addr.address1, addr.city, addr.province, addr.zip].filter(Boolean).join(", "),
      status:      order.fulfillment_status || "unfulfilled",
      payStatus:   order.financial_status || "pending",
      items:       (order.line_items||[]).map((l) => ({
        sku:      l.sku || String(l.variant_id || ""),
        name:     l.name,
        qty:      l.quantity || 1,
        price:    parseFloat(l.price || 0),
        variant:  l.variant_title || "",
      })),
    };
  }

  if (platform === "amazon") {
    const addr = order.ShippingAddress || order.shippingAddress || {};
    return {
      id:       order.AmazonOrderId || order.amazonOrderId || String(order.id||""),
      name:     order.AmazonOrderId || order.amazonOrderId || "AMZ-"+String(order.id||""),
      platform: "amazon",
      date:     order.PurchaseDate || order.purchaseDate || new Date().toISOString(),
      customer: order.BuyerInfo?.BuyerName || addr.Name || "Amazon Customer",
      phone:    addr.Phone || "",
      shipTo:   [addr.AddressLine1, addr.City, addr.StateOrRegion, addr.PostalCode].filter(Boolean).join(", "),
      status:   order.OrderStatus?.toLowerCase() || "pending",
      payStatus:order.PaymentMethod || "paid",
      items:    (order.items||order.OrderItems||[]).map((l) => ({
        sku:   l.SellerSKU || l.sellerSku || "",
        name:  l.Title     || l.title     || "",
        qty:   parseInt(l.QuantityOrdered || l.quantity || 1),
        price: parseFloat(l.ItemPrice?.Amount || l.price || 0),
        variant:"",
      })),
    };
  }

  if (platform === "flipkart") {
    return {
      id:       order.orderId || String(order.id||""),
      name:     "FK-"+(order.orderId||order.id||""),
      platform: "flipkart",
      date:     order.orderDate || new Date().toISOString(),
      customer: order.customerName || order.buyerName || "Flipkart Customer",
      phone:    order.phone || "",
      shipTo:   order.shippingAddress || "",
      status:   order.status?.toLowerCase() || "pending",
      payStatus:order.paymentType || "paid",
      items:    (order.items||order.orderItems||[]).map((l) => ({
        sku:   l.skuId   || l.sku     || "",
        name:  l.title   || l.name    || "",
        qty:   l.quantity|| 1,
        price: parseFloat(l.sellingPrice || l.price || 0),
        variant:"",
      })),
    };
  }

  if (platform === "myntra") {
    return {
      id:       String(order.orderId || order.id || ""),
      name:     "MYN-"+(order.orderId||order.id||""),
      platform: "myntra",
      date:     order.orderDate || new Date().toISOString(),
      customer: order.customerName || "Myntra Customer",
      phone:    order.phone || "",
      shipTo:   order.shippingAddress || "",
      status:   order.status?.toLowerCase() || "pending",
      payStatus:"paid",
      items:    (order.items||order.subOrders||[]).map((l) => ({
        sku:   l.sku     || "",
        name:  l.title   || l.name    || "",
        qty:   l.quantity|| 1,
        price: parseFloat(l.sellingPrice || l.price || 0),
        variant: l.size  || "",
      })),
    };
  }

  if (platform === "meesho") {
    return {
      id:       String(order.subOrderNo || order.order_id || ""),
      name:     "MSH-"+(order.subOrderNo||order.order_id||""),
      platform: "meesho",
      date:     order.order_date || new Date().toISOString(),
      customer: order.shipping_address?.customer_name || "Meesho Customer",
      phone:    order.shipping_address?.phone_number || "",
      shipTo:   [
        order.shipping_address?.address1,
        order.shipping_address?.city,
        order.shipping_address?.state,
        order.shipping_address?.zipcode
      ].filter(Boolean).join(", "),
      status:   order.status?.toLowerCase() || "pending",
      payStatus:"cod",
      items: [{
        sku:     order.sku      || "",
        name:    order.name     || "",
        qty:     order.quantity || 1,
        price:   parseFloat(order.price || 0),
        variant: order.size || "",
      }],
    };
  }

  return null;
}

// ── Convert normalized order → labels ─────────────────────────
function orderToLabels(order, skuDatabase) {
  const labels = [];
  (order.items || []).forEach((item) => {
    const dbRow = skuDatabase.find((r) =>
      r.sku && r.sku.toString().toLowerCase() === (item.sku||"").toString().toLowerCase()
    );
    for (let i = 0; i < item.qty; i++) {
      labels.push({
        orderId:  order.name,
        platform: order.platform,
        sku:      item.sku,
        asin:     dbRow?.asin || "",
        name:     item.name || dbRow?.name || "",
        mrp:      dbRow?.mrp || item.price.toFixed(0),
        origin:   dbRow?.origin || DEFAULT_ORIGIN,
        size:     dbRow?.size || item.variant || "",
        dims:     dbRow?.dims || "",
        mfg:      dbRow?.mfg || DEFAULT_MFG(),
        packer:   dbRow?.packer || DEFAULT_PACKER,
        addr:     dbRow?.addr || DEFAULT_ADDR,
        qtylabel: dbRow?.qtylabel || "1 Piece",
        brand:    dbRow?.brand   || DEFAULT_BRAND,
        tagline:  dbRow?.tagline || DEFAULT_TAGLINE,
        contact:  dbRow?.contact || DEFAULT_CONTACT,
        website:  dbRow?.website || DEFAULT_WEBSITE,
      });
    }
  });
  return labels;
}

function makeBarcodeUrl(text) {
  if (!text) return "";
  return `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(text)}&code=Code128&dpi=96&unit=px&imagetype=png&modulewidth=1&height=30&quietzonesize=5`;
}

// ── Single label ──────────────────────────────────────────────
function Label({ label, showShipping }) {
  const pl = PLATFORMS.find((p) => p.id === label.platform);
  return (
    <div style={{ width:192, height:192, border:"1px solid #ccc", padding:"7px 7px 5px 7px", display:"flex", flexDirection:"column", gap:2, background:"#fff", overflow:"hidden", position:"relative", boxSizing:"border-box", fontFamily:"Arial,sans-serif" }}>
      {pl && <div style={{ position:"absolute", top:3, right:3, background:pl.color, color:"#fff", fontSize:6, padding:"1px 4px", borderRadius:3, fontWeight:700 }}>{pl.name}</div>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        {label.sku ? <span style={{ display:"inline-flex", alignItems:"center", gap:3, background:"#1A3A5C", color:"#fff", borderRadius:3, padding:"2px 6px", fontSize:7.5, fontWeight:700, maxWidth:85, overflow:"hidden" }}><span style={{ fontSize:6, opacity:.7, marginRight:2 }}>SKU</span>{label.sku}</span> : <span/>}
        <span style={{ fontSize:12, fontWeight:"bold", color:"#111" }}>{label.mrp?"₹"+label.mrp:""}</span>
      </div>
      <div style={{ fontSize:8.5, fontWeight:"bold", textAlign:"center", lineHeight:1.2, padding:"2px 0", overflow:"hidden", maxHeight:22 }}>{label.name||"—"}</div>
      {label.asin && <>
        <div style={{ display:"flex", justifyContent:"center" }}><img src={makeBarcodeUrl(label.asin)} alt="barcode" style={{ width:148, height:30 }} onError={(e)=>e.target.style.display="none"}/></div>
        <div style={{ fontSize:6, color:"#777", textAlign:"center", fontFamily:"monospace" }}>{label.asin}</div>
      </>}
      <hr style={{ border:"none", borderTop:"0.5px solid #ddd", margin:"2px 0" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", gap:2 }}>
        {[label.origin&&["Origin",label.origin], label.size&&["Size",label.size], label.mfg&&["Mfg",label.mfg], label.qtylabel&&["Qty",label.qtylabel]].filter(Boolean).map(([l,v])=>(
          <div key={l} style={{ display:"flex", flexDirection:"column" }}>
            <span style={{ fontSize:5.5, color:"#aaa", textTransform:"uppercase" }}>{l}</span>
            <span style={{ fontSize:7, color:"#111", fontWeight:600 }}>{v}</span>
          </div>
        ))}
      </div>
      <hr style={{ border:"none", borderTop:"0.5px solid #ddd", margin:"2px 0" }}/>
      {/* Marketing / brand info only — no customer details */}
      <div style={{ marginTop:"auto" }}>
        <div style={{ fontSize:8, fontWeight:"bold", color:"#F47920", lineHeight:1.2 }}>{label.brand || "Leather Patta"}</div>
        <div style={{ fontSize:5.8, color:"#555", lineHeight:1.3, fontStyle:"italic" }}>{label.tagline || "Genuine Leather · Crafted in India"}</div>
        <div style={{ fontSize:5.8, color:"#333", lineHeight:1.3, marginTop:1 }}>{label.packer || "Vasudeva Enterprises"}</div>
        {label.contact && <div style={{ fontSize:5.5, color:"#666", lineHeight:1.3 }}>{label.contact}</div>}
        {label.website && <div style={{ fontSize:6, color:"#F47920", fontWeight:600 }}>{label.website}</div>}
      </div>
    </div>
  );
}

function LabelPage({ labels, showShipping }) {
  const cells = [...labels];
  while (cells.length < 6) cells.push(null);
  return (
    <div style={{ background:"#fff", width:384, height:576, display:"grid", gridTemplateColumns:"192px 192px", gridTemplateRows:"192px 192px 192px", border:"1px solid #ccc", borderRadius:4, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.12)" }}>
      {cells.map((label, i) => (
        <div key={i} style={{ borderRight:i%2===0?"1.5px dashed #bbb":"none", borderBottom:i<4?"1.5px dashed #bbb":"none" }}>
          {label ? <Label label={label} showShipping={showShipping}/> : <div style={{ width:192, height:192, background:"#f8f8f6" }}/>}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
export default function PageLabels({ toast, orders=[], tenant }) {
  const [tab,            setTab]            = useState("orders");
  const [allOrders,      setAllOrders]      = useState([]);
  const [loadingPlatform,setLoadingPlatform]= useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [labels,         setLabels]         = useState([]);
  const [skuDatabase,    setSkuDatabase]    = useState([]);
  const [dbLoaded,       setDbLoaded]       = useState(false);
  const [showShipping,   setShowShipping]   = useState(true);
  const [filter,         setFilter]         = useState("unfulfilled");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [dateFrom,       setDateFrom]       = useState(new Date().toISOString().split("T")[0]);
  const fileRef = useRef();

  // load SKU database from localStorage (shared with original label printer)
  useEffect(() => {
    try {
      const saved = localStorage.getItem("lp_db");
      const map   = localStorage.getItem("lp_colmap");
      if (saved && map) {
        const rows   = JSON.parse(saved);
        const colMap = JSON.parse(map);
        const db = rows.map((row) => {
          const g = (f) => colMap[f] != null ? String(row[colMap[f]]||"") : "";
          return { sku:g("sku"), asin:g("asin"), name:g("name"), mrp:g("mrp"), origin:g("origin")||DEFAULT_ORIGIN, size:g("size"), dims:g("dims"), mfg:g("mfg")||DEFAULT_MFG(), packer:g("packer")||DEFAULT_PACKER, addr:g("addr")||DEFAULT_ADDR, qtylabel:g("qtylabel")||"1 Piece" };
        });
        setSkuDatabase(db);
        setDbLoaded(true);
      }
    } catch(e) {}
  }, []);

  // load orders from all connected platforms
  useEffect(() => {
    loadAllPlatformOrders();
  }, []);

  const loadAllPlatformOrders = async () => {
    // start with Shopify orders already in props
    const normalized = orders.map((o) => normalizeOrder(o, "shopify")).filter(Boolean);
    setAllOrders(normalized);

    // fetch each connected platform
    const platforms = [
      { id:"amazon",   endpoint:"/amazon/orders",   key:"orders" },
      { id:"flipkart", endpoint:"/flipkart/orders",  key:"orders" },
      { id:"myntra",   endpoint:"/myntra/orders",    key:"orders" },
      { id:"meesho",   endpoint:"/meesho/orders",    key:"orders" },
    ];

    for (const pl of platforms) {
      setLoadingPlatform((prev) => ({ ...prev, [pl.id]:"loading" }));
      try {
        const data = await api(pl.endpoint);
        const raw  = data[pl.key] || data.payload?.Orders || data.orders || [];
        const norm = raw.map((o) => normalizeOrder(o, pl.id)).filter(Boolean);
        setAllOrders((prev) => [...prev.filter((o) => o.platform !== pl.id), ...norm]);
        setLoadingPlatform((prev) => ({ ...prev, [pl.id]:"ok" }));
      } catch(e) {
        setLoadingPlatform((prev) => ({ ...prev, [pl.id]:"error" }));
      }
    }
  };

  // filter orders
  const filteredOrders = allOrders.filter((o) => {
    if (platformFilter !== "all" && o.platform !== platformFilter) return false;
    if (filter === "unfulfilled") return !o.status || o.status === "unfulfilled" || o.status === "pending" || o.status === "approved";
    if (filter === "today") { const d=new Date(o.date); const t=new Date(); t.setHours(0,0,0,0); return d>=t; }
    if (filter === "date")  return new Date(o.date).toISOString().split("T")[0] === dateFrom;
    return true;
  });

  const toggleOrder  = (id) => setSelectedOrders((prev) => prev.includes(id) ? prev.filter((x)=>x!==id) : [...prev,id]);
  const selectAll    = () => setSelectedOrders(filteredOrders.map((o) => o.id));
  const selectNone   = () => setSelectedOrders([]);

  const generateLabels = () => {
    const selected = filteredOrders.filter((o) => selectedOrders.includes(o.id));
    if (!selected.length) { toast("Select at least one order","error"); return; }
    const all = selected.flatMap((o) => orderToLabels(o, skuDatabase));
    setLabels(all);
    setTab("preview");
    toast(all.length + " labels generated from " + selected.length + " orders ✓","success");
  };

  // upload SKU database
  const handleDbUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf);
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header:1, defval:"" });
      if (!rows.length) { toast("File is empty","error"); return; }
      const ALIASES = { sku:["sku","sku code","item code","product code"], asin:["asin","amazon asin"], name:["name","product name","item name","description","title"], mrp:["mrp","price","rate","selling price"], origin:["country of origin","origin","country"], size:["size","garment size","variant"], dims:["dimensions","dimension","dims"], mfg:["mfg date","manufacture date","mfg"], packer:["packer","packer name","packed by","manufacturer","brand"], addr:["address","packer address","packed at","factory address"], qtylabel:["qty label","quantity label","net quantity","net qty"] };
      const norm2 = (s) => String(s||"").toLowerCase().trim().replace(/[^a-z0-9 ]/g," ").replace(/\s+/g," ").trim();
      const colMap = {};
      rows[0].forEach((h,i) => { const hn=norm2(h); for (const [field,aliases] of Object.entries(ALIASES)) { if(!colMap[field]&&aliases.some((a)=>hn===a||hn.includes(a))) colMap[field]=i; } });
      const data = rows.slice(1).filter((r) => r.some((c) => String(c).trim()));
      localStorage.setItem("lp_db", JSON.stringify(data));
      localStorage.setItem("lp_colmap", JSON.stringify(colMap));
      localStorage.setItem("lp_dbname", file.name);
      const db = data.map((row) => { const g=(f)=>colMap[f]!=null?String(row[colMap[f]]||""):""; return { sku:g("sku"), asin:g("asin"), name:g("name"), mrp:g("mrp"), origin:g("origin")||DEFAULT_ORIGIN, size:g("size"), dims:g("dims"), mfg:g("mfg")||DEFAULT_MFG(), packer:g("packer")||DEFAULT_PACKER, addr:g("addr")||DEFAULT_ADDR, qtylabel:g("qtylabel")||"1 Piece" }; });
      setSkuDatabase(db); setDbLoaded(true);
      toast(db.length + " products loaded ✓","success");
    } catch(e) { toast("Failed to read file: "+e.message,"error"); }
    e.target.value = "";
  };

  // print all labels
  const printLabels = () => {
    const pages = [];
    for (let i=0; i<labels.length; i+=6) pages.push(labels.slice(i,i+6));
    const html = pages.map((page) => {
      const cells = [...page]; while(cells.length<6) cells.push(null);
      return `<div class="label-page">${cells.map((l) => l ? `
        <div class="label-cell">
          <div class="lc-platform" style="position:absolute;top:3px;right:3px;background:${PLATFORMS.find((p)=>p.id===l.platform)?.color||"#555"};color:#fff;font-size:6px;padding:1px 4px;border-radius:3px;font-weight:700">${PLATFORMS.find((p)=>p.id===l.platform)?.name||l.platform}</div>
          <div class="lc-toprow">
            ${l.sku?`<span class="lc-sku-box"><span class="lc-sku-label">SKU</span>${l.sku}</span>`:"<span></span>"}
            <span class="lc-mrp">${l.mrp?"₹"+l.mrp:""}</span>
          </div>
          <div class="lc-name">${l.name||"—"}</div>
          ${l.asin?`<div class="lc-barcode"><img src="${makeBarcodeUrl(l.asin)}" style="width:148px;height:30px"></div><div class="lc-asin">${l.asin}</div>`:""}
          <hr class="lc-hr">
          <div class="lc-inforow">
            ${l.origin?`<div class="lc-field"><div class="lc-fl">Origin</div><div class="lc-fv">${l.origin}</div></div>`:""}
            ${l.size?`<div class="lc-field"><div class="lc-fl">Size</div><div class="lc-fv">${l.size}</div></div>`:""}
            ${l.mfg?`<div class="lc-field"><div class="lc-fl">Mfg</div><div class="lc-fv">${l.mfg}</div></div>`:""}
            ${l.qtylabel?`<div class="lc-field"><div class="lc-fl">Qty</div><div class="lc-fv">${l.qtylabel}</div></div>`:""}
          </div>
          <hr class="lc-hr">
          <div class="lc-mkt">
            <div class="lc-brand">${l.brand||"Leather Patta"}</div>
            <div class="lc-tagline">${l.tagline||"Genuine Leather · Crafted in India"}</div>
            <div class="lc-pname">${l.packer||"Vasudeva Enterprises"}</div>
            ${l.contact?`<div class="lc-paddr">${l.contact}</div>`:""}
            ${l.website?`<div class="lc-web">${l.website}</div>`:""}
          </div>
        </div>` : `<div class="label-cell empty"></div>`).join("")}</div>`;
    }).join("");
    const win = window.open("","_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>3June Labels — ${new Date().toLocaleDateString("en-IN")}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;background:#fff;}.label-page{width:4in;height:6in;display:grid;grid-template-columns:2in 2in;grid-template-rows:2in 2in 2in;page-break-after:always;break-after:page;position:relative;}.label-cell{width:2in;height:2in;border-right:1.5px dashed #bbb;border-bottom:1.5px dashed #bbb;padding:7px 7px 5px 7px;display:flex;flex-direction:column;gap:2px;background:#fff;overflow:hidden;position:relative;}.label-cell:nth-child(2n){border-right:none;}.label-cell:nth-last-child(-n+2){border-bottom:none;}.label-cell.empty{background:#f8f8f6;}.lc-toprow{display:flex;justify-content:space-between;align-items:flex-start;}.lc-sku-box{display:inline-flex;align-items:center;gap:3px;background:#1A3A5C;color:#fff;border-radius:3px;padding:2px 6px;font-size:7.5px;font-weight:700;max-width:85px;overflow:hidden;}.lc-sku-label{font-size:6px;opacity:.7;margin-right:2px;}.lc-mrp{font-size:12px;font-weight:bold;color:#111;}.lc-name{font-size:8.5px;font-weight:bold;text-align:center;line-height:1.2;padding:2px 0;overflow:hidden;max-height:22px;}.lc-barcode{display:flex;justify-content:center;}.lc-asin{font-size:6px;color:#777;text-align:center;font-family:monospace;}.lc-hr{border:none;border-top:.5px solid #ddd;margin:2px 0;}.lc-inforow{display:flex;justify-content:space-between;gap:2px;}.lc-field{display:flex;flex-direction:column;}.lc-fl{font-size:5.5px;color:#aaa;text-transform:uppercase;}.lc-fv{font-size:7px;color:#111;font-weight:600;}.lc-pname{font-size:6.5px;font-weight:bold;color:#333;line-height:1.3;}.lc-paddr{font-size:5.8px;color:#666;line-height:1.3;}.lc-mkt{margin-top:auto;}.lc-brand{font-size:8px;font-weight:bold;color:#F47920;line-height:1.2;}.lc-tagline{font-size:5.8px;color:#555;font-style:italic;line-height:1.3;}.lc-web{font-size:6px;color:#F47920;font-weight:600;}@media print{*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style></head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(()=>win.print(),800);
    toast("Print dialog opened ✓","success");
  };

  const downloadTemplate = () => {
    const csv = [["SKU","ASIN","Product Name","MRP (INR)","Country of Origin","Size","Dimensions","Mfg Date (MM/YYYY)","Packer Name","Packer Address","Qty Label"],["LP-BLK-001","B0FN7PD59J","Leather Black Belt","499","India","Free Size","120x4x0.5 cm","04/2026","Vasudeva Enterprises","Tower 4C, Landcraft Golflinks, Pandav Nagar, Ghaziabad UP 201001","1 Piece"]].map((r)=>r.map((c)=>`"${c}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download="sellsync_label_template.csv"; a.click();
    toast("Template downloaded ✓","success");
  };

  const pages = [];
  for (let i=0; i<labels.length; i+=6) pages.push(labels.slice(i,i+6));

  // platform order counts
  const platformCounts = PLATFORMS.map((pl) => ({
    ...pl,
    count: allOrders.filter((o) => o.platform === pl.id).length,
    status: loadingPlatform[pl.id] || (pl.id==="shopify"?"ok":"idle"),
  }));

  return (
    <div>
      {/* header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>🏷️ Label Printer — All Platforms</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Auto-generate shipping & product labels from all platforms · 4×6 in · 6 per page</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btnOut} onClick={loadAllPlatformOrders}>↻ Refresh All</button>
          {labels.length > 0 && <button style={S.btn("#15803d")} onClick={printLabels}>🖨️ Print {labels.length} Labels</button>}
        </div>
      </div>

      {/* platform status row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
        {platformCounts.map((pl) => (
          <div key={pl.id} style={{ background:"#fff", borderRadius:8, border:`1px solid ${pl.color}30`, borderTop:`3px solid ${pl.color}`, padding:"10px 12px" }}>
            <div style={{ fontWeight:600, color:pl.color, fontSize:12, marginBottom:4 }}>{pl.icon} {pl.name}</div>
            <div style={{ fontSize:20, fontWeight:700, color:"#111" }}>{pl.count}</div>
            <div style={{ fontSize:10, color: pl.status==="loading"?"#d97706":pl.status==="ok"?"#15803d":pl.status==="error"?"#dc2626":"#9ca3af" }}>
              {pl.status==="loading"?"⏳ Loading…":pl.status==="ok"?"✓ Synced":pl.status==="error"?"⚠ Not connected":"– Not fetched"}
            </div>
          </div>
        ))}
      </div>

      {/* SKU database bar */}
      <div style={{ ...S.card, padding:14, marginBottom:14, borderLeft:`4px solid ${dbLoaded?"#22c55e":"#d97706"}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <div>
            <div style={{ fontWeight:600, fontSize:13 }}>
              {dbLoaded ? `✅ SKU Database — ${skuDatabase.length} products loaded` : "⚠️ No SKU Database — upload to auto-fill ASIN, MRP and barcode"}
            </div>
            <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Works across all platforms — one database for Shopify, Amazon, Flipkart, Myntra and Meesho</div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button style={S.btnOut} onClick={downloadTemplate}>⬇ Template</button>
            <button style={S.btn(dbLoaded?"#6b7280":"#F47920")} onClick={()=>fileRef.current?.click()}>
              {dbLoaded?"↻ Update Database":"⬆ Upload SKU Database"}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }} onChange={handleDbUpload}/>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        <button style={S.tab(tab==="orders")}  onClick={()=>setTab("orders")}>📦 Select Orders</button>
        <button style={S.tab(tab==="preview")} onClick={()=>setTab("preview")}>🏷️ Label Preview {labels.length>0?`(${labels.length})`:""}</button>
        <button style={S.tab(tab==="manual")}  onClick={()=>setTab("manual")}>✏️ Manual Label</button>
      </div>

      {/* ══ SELECT ORDERS ══ */}
      {tab==="orders" && (
        <>
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            {/* platform filter */}
            {[{id:"all",name:"All Platforms",color:"#374151"},...PLATFORMS].map((pl)=>(
              <button key={pl.id} style={{ ...S.btn(platformFilter===pl.id?pl.color:"#9ca3af"), padding:"5px 12px", fontSize:11 }}
                onClick={()=>setPlatformFilter(pl.id)}>{pl.id==="all"?"🌐 All":pl.id==="shopify"?"🏪":pl.id==="amazon"?"📦":pl.id==="flipkart"?"🛒":pl.id==="myntra"?"👗":"🛍️"} {pl.name}</button>
            ))}
            <div style={{ width:1, height:24, background:"#e5e7eb", margin:"0 4px" }}/>
            {/* status filter */}
            {[{id:"unfulfilled",label:"Pending"},{id:"today",label:"Today"},{id:"date",label:"By Date"},{id:"all",label:"All"}].map((f)=>(
              <button key={f.id} style={{ ...S.btnOut, fontSize:11, background:filter===f.id?"#f0f9ff":"#fff", borderColor:filter===f.id?"#2563eb":"#d1d5db", color:filter===f.id?"#2563eb":"#374151" }}
                onClick={()=>setFilter(f.id)}>{f.label}</button>
            ))}
            {filter==="date" && <input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}/>}
            <span style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af" }}>Labels show product + brand info only</span>
          </div>

          <div style={S.card}>
            <div style={S.cardHdr}>
              <span>Orders ({filteredOrders.length} across {new Set(filteredOrders.map((o)=>o.platform)).size} platforms)</span>
              <div style={{ display:"flex", gap:8 }}>
                <button style={{ ...S.btnOut, fontSize:11 }} onClick={selectAll}>Select All</button>
                <button style={{ ...S.btnOut, fontSize:11 }} onClick={selectNone}>Clear</button>
                <button style={{ ...S.btn(), opacity:selectedOrders.length===0?0.5:1 }} onClick={generateLabels} disabled={selectedOrders.length===0}>
                  🏷️ Generate Labels ({selectedOrders.length})
                </button>
              </div>
            </div>

            {filteredOrders.length===0 ? (
              <div style={{ textAlign:"center", padding:50, color:"#9ca3af" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📦</div>
                No orders found — try changing filters or clicking Refresh All
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    <th style={S.th}><input type="checkbox" checked={selectedOrders.length===filteredOrders.length&&filteredOrders.length>0} onChange={(e)=>e.target.checked?selectAll():selectNone()}/></th>
                    {["Platform","Order","Date","Customer","Items","Labels","Status"].map((h)=><th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {filteredOrders.map((o)=>{
                      const pl = PLATFORMS.find((p)=>p.id===o.platform);
                      const checked = selectedOrders.includes(o.id);
                      const labelCount = o.items.reduce((a,l)=>a+l.qty,0);
                      return (
                        <tr key={o.id} style={{ background:checked?"#fff8f2":"", cursor:"pointer" }}
                          onClick={()=>toggleOrder(o.id)}
                          onMouseEnter={(e)=>{ if(!checked) e.currentTarget.style.background="#fafafa"; }}
                          onMouseLeave={(e)=>{ e.currentTarget.style.background=checked?"#fff8f2":""; }}>
                          <td style={S.td}><input type="checkbox" checked={checked} onChange={()=>toggleOrder(o.id)} onClick={(e)=>e.stopPropagation()}/></td>
                          <td style={S.td}>
                            {pl && <span style={{ background:pl.color, color:"#fff", padding:"2px 7px", borderRadius:10, fontSize:10, fontWeight:700 }}>{pl.icon} {pl.name}</span>}
                          </td>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920", fontWeight:600 }}>{o.name}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(o.date).toLocaleDateString("en-IN")}</td>
                          <td style={{ ...S.td, fontWeight:500 }}>{o.customer}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{o.items.slice(0,2).map((l)=>l.name+"×"+l.qty).join(", ")}</td>
                          <td style={S.td}><span style={{ background:"#eff6ff", color:"#2563eb", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{labelCount} label{labelCount!==1?"s":""}</span></td>
                          <td style={S.td}><span style={{ fontSize:10, background:o.status==="fulfilled"||o.status==="delivered"?"#f0fdf4":"#fffbeb", color:o.status==="fulfilled"||o.status==="delivered"?"#15803d":"#d97706", padding:"2px 8px", borderRadius:10, fontWeight:600 }}>{o.status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══ PREVIEW ══ */}
      {tab==="preview" && (
        labels.length===0
          ? <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🏷️</div>
              <div style={{ fontWeight:600, fontSize:16, marginBottom:8 }}>No labels yet</div>
              <button style={S.btn()} onClick={()=>setTab("orders")}>← Select Orders</button>
            </div>
          : <>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
                <div style={{ fontSize:13, color:"#6b7280" }}>{labels.length} labels · {pages.length} page{pages.length!==1?"s":""} · 4×6 in</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btnOut} onClick={()=>{setLabels([]);setTab("orders");}}>← Back</button>
                  <button style={S.btn("#15803d")} onClick={printLabels}>🖨️ Print All Labels</button>
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:18, alignItems:"center" }}>
                {pages.map((page,pi)=>(
                  <div key={pi}>
                    {pi>0&&<div style={{ textAlign:"center",fontSize:12,color:"#9ca3af",marginBottom:10 }}>— Page {pi+1} —</div>}
                    <LabelPage labels={page} showShipping={showShipping}/>
                  </div>
                ))}
              </div>
              <div style={{ ...S.card, marginTop:20 }}>
                <div style={S.cardHdr}><span>Label Details</span></div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["#","Order","Platform","SKU","Product","Size","MRP","Ship To"].map((h)=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {labels.map((label,i)=>{
                        const pl=PLATFORMS.find((p)=>p.id===label.platform);
                        return (
                          <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                            <td style={S.td}><span style={{ color:"#9ca3af", fontSize:11 }}>{i+1}</span></td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{label.orderId}</td>
                            <td style={S.td}>{pl&&<span style={{ background:pl.color, color:"#fff", padding:"2px 6px", borderRadius:8, fontSize:10, fontWeight:700 }}>{pl.name}</span>}</td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{label.sku}</td>
                            <td style={{ ...S.td, fontWeight:500, fontSize:12 }}>{label.name}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{label.size}</td>
                            <td style={{ ...S.td, fontWeight:600 }}>₹{label.mrp}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
      )}

      {/* ══ MANUAL ══ */}
      {tab==="manual" && (
        <ManualLabel toast={toast} skuDatabase={skuDatabase} showShipping={showShipping}
          onGenerate={(label)=>{ setLabels((prev)=>[...prev,label]); setTab("preview"); toast("Label added ✓","success"); }}/>
      )}
    </div>
  );
}

// ── Manual Product Label Builder ─────────────────────────────
function ManualLabel({ toast, skuDatabase, showShipping, onGenerate }) {


  const blankRow = () => ({
    id:         Date.now()+Math.random(),
    sku:        "", name:        "", mrp:         "",
    asin:       "", size:        "Free Size",
    origin:     "India", material: "Genuine Leather",
    color:      "", dims:        "", mfg:         DEFAULT_MFG(),
    packer:     DEFAULT_PACKER, addr: DEFAULT_ADDR,
    qtylabel:   "1 Piece", copies: "1",
    hsn:        "42033000", gstRate: "18",
    marketedBy: "Leather Patta",
    email:      "support@leatherpatta.com",
    website:    "leatherpatta.com",
  });

  const SIZES     = ["Free Size","28","30","32","34","36","38","40","42","44","S","M","L","XL","XXL"];
  const MATERIALS = ["Genuine Leather","PU Leather","Canvas","Nylon","Faux Leather","Fabric"];

  const [rows,       setRows]      = useState([blankRow()]);
  const [showBulk,   setShowBulk]  = useState(false);
  const [bulkText,   setBulkText]  = useState("");
  const [copies,     setCopies]    = useState(1);
  const [labelSize,  setLabelSize] = useState("2x2"); // 2x2 inches or 2x3

  const setR = (id,k,v) => setRows(prev=>prev.map(r=>r.id===id?{...r,[k]:v}:r));
  const addRow    = () => setRows(prev=>[...prev,blankRow()]);
  const removeRow = (id) => setRows(prev=>prev.filter(r=>r.id!==id));
  const copyRow   = (row) => setRows(prev=>[...prev,{...row,id:Date.now()+Math.random()}]);

  const lookupSku = (id, sku) => {
    const row = (skuDatabase||[]).find(r=>r.sku?.toLowerCase()===sku.toLowerCase());
    if (!row) { toast("SKU not found in database","error"); return; }
    setRows(prev=>prev.map(r=>r.id===id?{...r,...row,id}:r));
    toast("SKU auto-filled ✓","success");
  };

  const parseBulk = () => {
    const lines = bulkText.trim().split("\n").filter(l=>l.trim());
    const parsed = lines.map(line=>{
      const p = line.split("\t").map(s=>s.trim());
      return {...blankRow(), id:Date.now()+Math.random(),
        sku:p[0]||"", name:p[1]||"", mrp:p[2]||"", asin:p[3]||"",
        size:p[4]||"Free Size", origin:p[5]||"India", mfg:p[6]||DEFAULT_MFG(),
        copies:p[7]||"1",
      };
    });
    setRows(parsed);
    setShowBulk(false);
    setBulkText("");
    toast(parsed.length+" rows imported ✓","success");
  };

  // Build one label cell HTML
  const buildLabelCell = (row) => {
    return `
      <div class="label-cell">

        <!-- ── BRAND HEADER ── -->
        <div class="lc-brand">
          <span class="lc-brand-name">${row.marketedBy||"Leather Patta"}</span>
          <span class="lc-sku-pill">${row.sku||"—"}</span>
        </div>

        <!-- ── PRODUCT NAME ── -->
        <div class="lc-name">${row.name||"Product Name"}</div>

        <!-- ── MRP LINE ── -->
        <div class="lc-mrp-line">
          <span class="lc-mrp-label">MRP:</span>
          <span class="lc-mrp-val">₹${row.mrp||"0"}</span>
          <span class="lc-mrp-note">(Incl. of all taxes)</span>
        </div>

        <hr class="lc-hr"/>

        <!-- ── PRODUCT DETAILS ── -->
        <div class="lc-detail-row"><span class="lc-dl">Color</span><span class="lc-dv">${row.color||"—"}</span></div>
        <div class="lc-detail-row"><span class="lc-dl">Size</span><span class="lc-dv">${row.size||"Free Size"}</span></div>
        <div class="lc-detail-row"><span class="lc-dl">Material</span><span class="lc-dv">${row.material||"Genuine Leather"}</span></div>
        <div class="lc-detail-row"><span class="lc-dl">Country of Origin</span><span class="lc-dv">${row.origin||"India"}</span></div>
        <div class="lc-detail-row"><span class="lc-dl">Net Quantity</span><span class="lc-dv">${row.qtylabel||"1 Piece"}</span></div>
        ${row.dims?`<div class="lc-detail-row"><span class="lc-dl">Dimensions</span><span class="lc-dv">${row.dims}</span></div>`:""}
        <div class="lc-detail-row"><span class="lc-dl">Mfg / Best Before</span><span class="lc-dv">${row.mfg||""}</span></div>

        <hr class="lc-hr"/>

        <!-- ── GST / HSN ── -->
        <div class="lc-gst-row">
          <span>HSN: <strong>${row.hsn||"42033000"}</strong></span>
          <span>GST: <strong>${row.gstRate||"18"}%</strong></span>
          ${row.asin?`<span>ASIN: <strong>${row.asin}</strong></span>`:""}
        </div>

        <!-- ── BARCODE ── -->
        <div class="lc-barcode">
          <div class="lc-barcode-text">${(row.sku||"").padEnd(12,"0").slice(0,13)}</div>
        </div>

        <hr class="lc-hr"/>

        <!-- ── MANUFACTURER BLOCK ── -->
        <div class="lc-mfr">
          <div class="lc-mfr-head">Marketed By: <strong>${row.marketedBy||"Leather Patta"}</strong></div>
          <div class="lc-mfr-line">${row.addr||DEFAULT_ADDR}</div>
          ${row.email?`<div class="lc-mfr-line">✉ ${row.email}</div>`:""}
          ${row.website?`<div class="lc-mfr-line">🌐 ${row.website}</div>`:""}
        </div>

      </div>`;
  };

  const printLabels = () => {
    const valid = rows.filter(r=>r.name||r.sku);
    if (!valid.length) { toast("Add at least one product","error"); return; }

    // Expand rows by copies count
    const expanded = [];
    valid.forEach(r=>{
      const n = Math.max(1, Math.min(100, parseInt(r.copies)||1));
      for (let i=0;i<n;i++) expanded.push(r);
    });

    // Group into pages of 6 (2×3 grid)
    const perPage = 6;
    const pages   = [];
    for (let i=0;i<expanded.length;i+=perPage) pages.push(expanded.slice(i,i+perPage));

    const pagesHTML = pages.map(pg=>{
      const cells = pg.map(r=>buildLabelCell(r)).join("");
      // Pad to 6 cells
      const empties = Array(perPage-pg.length).fill('<div class="label-cell empty"></div>').join("");
      return `<div class="label-page">${cells}${empties}</div>`;
    }).join("");

    const win = window.open("","_blank","width=650,height=800");
    win.document.write(`<!DOCTYPE html><html><head>
    <title>3June Product Labels</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;background:#fff}
      .label-page{width:4in;height:6in;display:grid;grid-template-columns:2in 2in;grid-template-rows:2in 2in 2in;page-break-after:always;break-after:page;position:relative}
      /* ── Label cell ── */
      .label-cell{width:2in;min-height:2in;border-right:1.5px dashed #bbb;border-bottom:1.5px dashed #bbb;padding:5px 6px;display:flex;flex-direction:column;gap:1.5px;background:#fff;overflow:hidden;font-family:Arial,sans-serif}
      .label-cell:nth-child(2n){border-right:none}
      .label-cell:nth-last-child(-n+2){border-bottom:none}
      .label-cell.empty{background:#f8f8f8}

      /* Brand header */
      .lc-brand{display:flex;justify-content:space-between;align-items:center;margin-bottom:1px}
      .lc-brand-name{font-size:8px;font-weight:700;color:#1A3A5C;letter-spacing:.3px;text-transform:uppercase}
      .lc-sku-pill{font-size:6px;background:#1A3A5C;color:#fff;padding:1px 5px;border-radius:3px;font-family:monospace;font-weight:700}

      /* Product name */
      .lc-name{font-size:8px;font-weight:700;color:#111;line-height:1.3;text-align:center;padding:1px 0}

      /* MRP line */
      .lc-mrp-line{display:flex;align-items:baseline;gap:3px;justify-content:center;padding:1px 0}
      .lc-mrp-label{font-size:7px;color:#555;font-weight:600}
      .lc-mrp-val{font-size:11px;font-weight:700;color:#111}
      .lc-mrp-note{font-size:6px;color:#888}

      /* Detail rows */
      .lc-detail-row{display:flex;justify-content:space-between;align-items:baseline;padding:.5px 0}
      .lc-dl{font-size:6px;color:#888;flex-shrink:0;margin-right:3px}
      .lc-dv{font-size:7px;font-weight:600;color:#111;text-align:right}

      /* GST row */
      .lc-gst-row{display:flex;gap:6px;font-size:6px;color:#555;justify-content:center;padding:1px 0}
      .lc-gst-row strong{color:#111}

      /* Barcode */
      .lc-barcode{display:flex;justify-content:center;margin:1.5px 0}
      .lc-barcode-text{border:1px solid #ccc;padding:1px 8px;border-radius:2px;font-size:7px;font-family:monospace;color:#333;letter-spacing:2.5px}

      /* HR */
      .lc-hr{border:none;border-top:.5px solid #ddd;margin:2px 0}

      /* Manufacturer block */
      .lc-mfr{background:#f7f7f7;border-radius:2px;padding:2px 4px;margin-top:auto}
      .lc-mfr-head{font-size:6.5px;color:#333;line-height:1.4}
      .lc-mfr-head strong{color:#1A3A5C}
      .lc-mfr-line{font-size:5.5px;color:#666;line-height:1.4}
      @media print{
        *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        @page{size:4in 6in;margin:0}
        body{margin:0}
      }
    </style></head><body>${pagesHTML}</body></html>`);
    win.document.close();
    setTimeout(()=>win.print(),600);
    toast(`${expanded.length} labels sent to printer ✓`,"success");
  };

  const downloadTemplate = () => {
    const csv = [
      ["SKU","Product Name","MRP (₹)","ASIN","Size","Material","Color","Origin","Mfg Date (MM/YYYY)","HSN","GST%","Packer Name","Packer Address","Net Qty","Dimensions","Copies"],
      ["LP-BLK-001","Black Formal Belt","899","B0FN7PD59J","Free Size","Genuine Leather","Black","India","04/2026","42033000","18","Vasudeva Enterprises","Tower 4C, Ghaziabad UP 201001","1 Piece","120x4x0.5 cm","1"],
    ].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download="3june_label_template.csv"; a.click();
    toast("Template downloaded ✓","success");
  };

  const SI = { padding:"5px 7px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", outline:"none", width:"100%" };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:14 }}>🏷️ Manual Product Label</div>
          <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Generate product hang tags / price tags without platform sync</div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button style={{ padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" }} onClick={downloadTemplate}>⬇ Download Template</button>
          <button style={{ padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#eff6ff", color:"#2563eb", border:"1px solid #bfdbfe", fontFamily:"inherit" }} onClick={()=>setShowBulk(!showBulk)}>📋 Bulk Import</button>
          <button style={{ padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#f3f4f6", color:"#374151", border:"1px solid #e5e7eb", fontFamily:"inherit" }} onClick={addRow}>+ Add Product</button>
          <button style={{ padding:"8px 18px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#F47920", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:700 }}
            onClick={printLabels}>🖨️ Print Labels ({rows.filter(r=>r.name||r.sku).reduce((a,r)=>a+(parseInt(r.copies)||1),0)})</button>
        </div>
      </div>

      {/* Bulk import */}
      {showBulk && (
        <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:16, marginBottom:14 }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>📋 Bulk Import</div>
          <div style={{ background:"#f9fafb", borderRadius:6, padding:"7px 10px", fontSize:10, fontFamily:"monospace", color:"#6b7280", marginBottom:10 }}>
            SKU [TAB] Product Name [TAB] MRP [TAB] ASIN [TAB] Size [TAB] Origin [TAB] Mfg Date [TAB] Copies
          </div>
          <textarea style={{ ...SI, height:90, resize:"vertical", marginBottom:10, fontFamily:"monospace" }}
            placeholder={"LP-BLK-001\tBlack Formal Belt\t899\tB0FN7PD59J\tFree Size\tIndia\t04/2026\t2"}
            value={bulkText} onChange={e=>setBulkText(e.target.value)}/>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ padding:"7px 16px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#2563eb", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }} onClick={parseBulk}>Import</button>
            <button style={{ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" }} onClick={()=>setShowBulk(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Product rows */}
      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:1100 }}>
            <thead>
              <tr>
                {["SKU","Product Name *","MRP ₹ *","Color","Size","Material","Origin","Mfg Date","HSN","GST%","Net Qty","Marketed By","Email","Website","Copies",""].map(h=>(
                  <th key={h} style={{ padding:"7px 8px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row,i)=>(
                <tr key={row.id} style={{ background:i%2?"#fafafa":"#fff" }}>
                  <td style={{ padding:"4px 6px" }}>
                    <div style={{ display:"flex", gap:4 }}>
                      <input style={{ ...SI, width:90 }} placeholder="LP-BLK-001" value={row.sku} onChange={e=>setR(row.id,"sku",e.target.value)}/>
                      <button style={{ padding:"4px 6px", borderRadius:5, fontSize:10, cursor:"pointer", background:"#f3f4f6", border:"1px solid #e5e7eb", fontFamily:"inherit", whiteSpace:"nowrap" }}
                        onClick={()=>lookupSku(row.id,row.sku)} title="Auto-fill from SKU database">🔍</button>
                    </div>
                  </td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:180 }} placeholder="Black Formal Leather Belt" value={row.name} onChange={e=>setR(row.id,"name",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input type="number" style={{ ...SI, width:70 }} placeholder="899" value={row.mrp} onChange={e=>setR(row.id,"mrp",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:110 }} placeholder="B0FN7PD59J" value={row.asin} onChange={e=>setR(row.id,"asin",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}>
                    <select style={{ ...SI, width:90 }} value={row.size} onChange={e=>setR(row.id,"size",e.target.value)}>
                      {SIZES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:"4px 6px" }}>
                    <select style={{ ...SI, width:110 }} value={row.material} onChange={e=>setR(row.id,"material",e.target.value)}>
                      {MATERIALS.map(m=><option key={m}>{m}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:70 }} placeholder="Black" value={row.color} onChange={e=>setR(row.id,"color",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:60 }} value={row.origin} onChange={e=>setR(row.id,"origin",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:80 }} placeholder="04/2026" value={row.mfg} onChange={e=>setR(row.id,"mfg",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:75 }} value={row.hsn} onChange={e=>setR(row.id,"hsn",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}>
                    <select style={{ ...SI, width:60 }} value={row.gstRate} onChange={e=>setR(row.id,"gstRate",e.target.value)}>
                      {["0","5","12","18","28"].map(r=><option key={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:80 }} placeholder="1 Piece" value={row.qtylabel} onChange={e=>setR(row.id,"qtylabel",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:120 }} placeholder="Leather Patta" value={row.marketedBy||""} onChange={e=>setR(row.id,"marketedBy",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:150 }} placeholder="support@leatherpatta.com" value={row.email||""} onChange={e=>setR(row.id,"email",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input style={{ ...SI, width:130 }} placeholder="leatherpatta.com" value={row.website||""} onChange={e=>setR(row.id,"website",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}><input type="number" min="1" max="100" style={{ ...SI, width:55 }} value={row.copies} onChange={e=>setR(row.id,"copies",e.target.value)}/></td>
                  <td style={{ padding:"4px 6px" }}>
                    <div style={{ display:"flex", gap:4 }}>
                      <button title="Duplicate row" style={{ padding:"4px 7px", borderRadius:5, fontSize:11, cursor:"pointer", background:"#eff6ff", color:"#2563eb", border:"none", fontFamily:"inherit" }}
                        onClick={()=>copyRow(row)}>⧉</button>
                      <button title="Remove row" style={{ padding:"4px 7px", borderRadius:5, fontSize:11, cursor:"pointer", background:"#fef2f2", color:"#dc2626", border:"none", fontFamily:"inherit" }}
                        onClick={()=>removeRow(row.id)}>×</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding:"10px 16px", borderTop:"1px solid #e5e7eb", display:"flex", gap:10, alignItems:"center" }}>
          <button style={{ padding:"6px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#f3f4f6", color:"#374151", border:"1px solid #e5e7eb", fontFamily:"inherit" }} onClick={addRow}>+ Add Product</button>
          <span style={{ fontSize:11, color:"#9ca3af" }}>
            {rows.filter(r=>r.name||r.sku).length} products · {rows.reduce((a,r)=>a+(parseInt(r.copies)||1),0)} total labels
          </span>
        </div>
      </div>

      {/* Info box */}
      <div style={{ marginTop:10, background:"#f0fdf4", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#15803d" }}>
        💡 <strong>Label format:</strong> 2×2 inch labels, 6 per A4/Letter page (2 columns × 3 rows) · 
        Each label includes: SKU, MRP, product name, barcode, size, material, origin, Mfg date, HSN, GST%, packer name & address · 
        <strong>Copies</strong> column lets you print multiple labels for the same product
      </div>
    </div>
  );
}